import React, { useEffect, useMemo, useRef } from 'react';
import { buildImageReadApiUrl } from './mosidaApi';
import {
  isArtworkDisabledForProduct,
  resolveArtworkPlacement,
} from './productArtworkPlacements';
import { detectImageContentBounds } from './imageContentBounds';

function drawRectForFit(image, bounds, fit) {
  if (fit === 'stretch') return bounds;
  const imageAspect = image.naturalWidth / image.naturalHeight;
  const boundsAspect = bounds.width / bounds.height;
  const fitByWidth =
    fit === 'contain' ? imageAspect > boundsAspect : imageAspect < boundsAspect;
  if (fitByWidth) {
    const height = bounds.width / imageAspect;
    return {
      x: bounds.x,
      y: bounds.y + (bounds.height - height) / 2,
      width: bounds.width,
      height,
    };
  }
  const width = bounds.height * imageAspect;
  return {
    x: bounds.x + (bounds.width - width) / 2,
    y: bounds.y,
    width,
    height: bounds.height,
  };
}

const imageCache = new Map();

function loadImageFromUrl(url, crossOrigin) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) image.crossOrigin = crossOrigin;
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    image.src = url;
  });
}

/**
 * Prefer fetching via the image-processing proxy into a blob URL.
 * Avoids CORS on CloudFront (no ACAO) and on proxy→CDN redirects.
 */
async function loadImageViaProxyBlob(source) {
  const proxied = buildImageReadApiUrl(source);
  const response = await fetch(proxied, {
    method: 'GET',
    headers: { accept: 'image/*' },
  });
  if (!response.ok) {
    throw new Error(`image-processing/read ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await loadImageFromUrl(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(source) {
  const key = String(source || '').trim();
  if (!key) return Promise.reject(new Error('Failed to load image: empty source'));
  const cached = imageCache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    if (/^(data:|blob:)/i.test(key)) {
      return loadImageFromUrl(key);
    }
    try {
      return await loadImageViaProxyBlob(key);
    } catch {
      // Last resort for non-canvas display; may taint canvas if CORS is missing.
      return loadImageFromUrl(key);
    }
  })().catch((error) => {
    imageCache.delete(key);
    throw error;
  });

  imageCache.set(key, promise);
  return promise;
}

export function preloadImages(urls) {
  (urls || []).forEach((url) => {
    if (url) void loadImage(url).catch(() => {});
  });
}

function drawImage(ctx, image, bounds, fit) {
  const rect = drawRectForFit(image, bounds, fit);
  ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  return rect;
}

function resolveArtworkReference(product, productRect, placement, fullCanvas) {
  if (placement.areaRelativeTo === 'canvas') return fullCanvas;
  const bounds = detectImageContentBounds(product);
  return {
    x: productRect.x + bounds.x * productRect.width,
    y: productRect.y + bounds.y * productRect.height,
    width: bounds.width * productRect.width,
    height: bounds.height * productRect.height,
  };
}

function drawArtwork(ctx, image, placement, reference) {
  const bounds = {
    x: reference.x + placement.area.x * reference.width,
    y: reference.y + placement.area.y * reference.height,
    width: placement.area.width * reference.width,
    height: placement.area.height * reference.height,
  };
  ctx.save();
  ctx.globalAlpha = placement.artworkOpacity;
  ctx.translate(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
  ctx.rotate((placement.artworkRotation * Math.PI) / 180);
  drawImage(
    ctx,
    image,
    {
      x: -bounds.width / 2,
      y: -bounds.height / 2,
      width: bounds.width,
      height: bounds.height,
    },
    placement.artworkFit
  );
  ctx.restore();
}

function ProductArtworkPreview({
  productImage,
  artworkImage,
  productName,
  skuCode,
  placement: placementInput,
  className,
  style,
}) {
  const canvasRef = useRef(null);
  const placement = useMemo(
    () => resolveArtworkPlacement(skuCode, placementInput),
    [skuCode, placementInput]
  );
  const artworkDisabled = useMemo(
    () => isArtworkDisabledForProduct(productName),
    [productName]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const resolvedProductImage = placement.templateImage || productImage;
    const rawArtwork =
      typeof artworkImage === 'string'
        ? artworkImage.trim()
        : String(artworkImage || '').trim();
    const resolvedArtwork = artworkDisabled ? '' : rawArtwork;
    if (!canvas || !resolvedProductImage) return undefined;

    let cancelled = false;
    const logicalWidth = 600;
    const logicalHeight = Math.round(logicalWidth / placement.canvasAspectRatio);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const nextWidth = Math.round(logicalWidth * dpr);
    const nextHeight = Math.round(logicalHeight * dpr);
    if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
      canvas.width = nextWidth;
      canvas.height = nextHeight;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const render = async () => {
      try {
        const [product, artwork] = await Promise.all([
          loadImage(resolvedProductImage),
          resolvedArtwork ? loadImage(resolvedArtwork) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        ctx.clearRect(0, 0, logicalWidth, logicalHeight);
        const fullCanvas = { x: 0, y: 0, width: logicalWidth, height: logicalHeight };
        if (artwork && placement.artworkLayer === 'below-product') {
          const productRect = drawRectForFit(product, fullCanvas, placement.productFit);
          drawArtwork(
            ctx,
            artwork,
            placement,
            resolveArtworkReference(product, productRect, placement, fullCanvas)
          );
          ctx.save();
          ctx.globalCompositeOperation = placement.productCompositeOperation;
          drawImage(ctx, product, fullCanvas, placement.productFit);
          ctx.restore();
        } else {
          const productRect = drawImage(ctx, product, fullCanvas, placement.productFit);
          if (artwork) {
            drawArtwork(
              ctx,
              artwork,
              placement,
              resolveArtworkReference(product, productRect, placement, fullCanvas)
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to render product artwork preview.', error);
        }
      }
    };

    void render();
    return () => {
      cancelled = true;
    };
  }, [artworkDisabled, artworkImage, placement, productImage]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        display: "block",
        width: "100%",
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
        ...style,
      }}
    />
  );
}

export default ProductArtworkPreview;
